import { t, intlLocale } from '@procumi/shared';
import type { PerfMonth, SavingsSummary } from '@procumi/shared';
import { useEffect, useRef, useState } from 'react';
import { I, money, Num, pct, Section, shortMoney } from './shared';
import { rich } from '../../lib/rich';

/**
 * „Oportunitatea de achiziție”: banii câștigați sau lăsați pe masă. Cifrele
 * sunt principalul — economia posibilă, cea obținută, cât s-a captat — iar
 * graficul mic de dedesubt arată, pe luni, prețul de referință (a doua ofertă
 * completă) față de cel mai bun preț. Economia posibilă nu e bani economisiți:
 * devine „obținută” doar când comanzi la cel mai bun preț.
 */
export function SavingsPanel({
  months,
  savings,
  rangeLabel,
  onOpenRfq,
  onAll,
}: {
  months: PerfMonth[];
  savings: SavingsSummary;
  rangeLabel: string;
  onOpenRfq: (id: string) => void;
  onAll: () => void;
}) {
  const top = savings.top.find((t) => !t.decided) ?? savings.top[0];
  const share = savings.quoted ? savings.potential / savings.quoted : 0;
  return (
    <Section
      icon="coins"
      tone="green"
      title={t('Oportunitatea de achiziție')}
      sub={t('Din comparațiile ofertelor, {0}', rangeLabel.toLowerCase())}
      className="db-save"
      id="db-save"
      action={
        <button type="button" className="link" onClick={onAll}>
          {t('Vezi economiile →')}
        </button>
      }
    >
      <div className="db-save-nums">
        <div className="main">
          <span className="db-lbl">{t('Economii posibile')}</span>
          <b>
            <Num value={savings.potential} format={money} />
          </b>
          <small>
            {savings.potentialCount
              ? t(
                  '{0}% sub prețul de referință, la {1} {2}',
                  (share * 100).toLocaleString(intlLocale(), { maximumFractionDigits: 1 }),
                  savings.potentialCount,
                  savings.potentialCount === 1 ? t('cerere comparată') : t('cereri comparate'),
                )
              : t('nicio cerere cu două oferte complete în perioadă')}
          </small>
        </div>
        <div>
          <span className="db-lbl">{t('Economii obținute')}</span>
          <b className="green">
            <Num value={savings.realized} format={money} />
          </b>
          <small>
            {t(
              'la {0} {1}',
              savings.realizedCount,
              savings.realizedCount === 1 ? t('comandă') : t('comenzi'),
            )}
          </small>
        </div>
        <div>
          <span className="db-lbl">{t('Captat')}</span>
          <b>{savings.captured == null ? '—' : pct(savings.captured)}</b>
          <span className="db-capt-t" aria-hidden="true">
            <span style={{ width: savings.captured == null ? '0%' : pct(savings.captured) }} />
          </span>
        </div>
      </div>
      <MiniChart months={months} />
      {top ? (
        <button type="button" className="db-opp" onClick={() => onOpenRfq(top.rfq.id)}>
          <span className="db-lbl">
            {top.decided ? t('Cea mai mare diferență') : t('Cea mai mare oportunitate deschisă')}
          </span>
          <span className="db-opp-r">
            <b>
              {top.rfq.title} <span>· {top.rfq.site.name}</span>
            </b>
            <em>{money(top.potential)}</em>
          </span>
          <I d="arrow" size={14} />
        </button>
      ) : null}
      <p className="db-note">
        {t('Economia posibilă nu e garantată: devine obținută când comanzi la cel mai bun preț.')}
      </p>
    </Section>
  );
}

/** Graficul mic: preț de referință (gri) și cel mai bun preț (albastru), zona dintre ele în verde. */
function MiniChart({ months }: { months: PerfMonth[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(480);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(240, Math.round(e!.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const H = 132;
  const P = { l: 44, r: 8, t: 8, b: 20 };
  const max = Math.max(...months.map((m) => m.reference), 1);
  const nice = niceMax(max);
  const step = (W - P.l - P.r) / Math.max(months.length - 1, 1);
  const x = (i: number) => P.l + i * step;
  const y = (v: number) => P.t + (1 - v / nice) * (H - P.t - P.b);
  const line = (k: 'reference' | 'best') =>
    months.map((m, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(m[k]).toFixed(1)}`).join(' ');
  const band = `${line('reference')} ${[...months]
    .reverse()
    .map((m, j) => `L${x(months.length - 1 - j).toFixed(1)},${y(m.best).toFixed(1)}`)
    .join(' ')}Z`;
  const h = hover != null ? months[hover] : null;
  return (
    <div className="db-mini-chart" ref={box} onMouseLeave={() => setHover(null)}>
      <div className="db-legend row">
        <span>
          <i className="ref" /> {t('preț de referință')}
        </span>
        <span>
          <i className="best" /> {t('cel mai bun preț')}
        </span>
        <span>
          <i className="gap" /> {t('economie posibilă')}
        </span>
      </div>
      {months.every((m) => !m.rfqs) ? (
        <div className="db-empty">
          {t('Graficul se umple din cererile cu cel puțin două oferte complete.')}
        </div>
      ) : (
        <div className="db-chart">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            width={W}
            height={H}
            role="img"
            aria-label={t('Preț de referință și cel mai bun preț, pe luni')}
          >
            {[0, 1].map((f) => (
              <g key={f}>
                <line x1={P.l} x2={W - P.r} y1={y(nice * f)} y2={y(nice * f)} className="grid" />
                <text x={P.l - 6} y={y(nice * f) + 4} textAnchor="end" className="ax">
                  {shortMoney(nice * f)}
                </text>
              </g>
            ))}
            <path d={band} className="band" />
            <path d={line('reference')} className="ref" />
            <path d={line('best')} className="best" />
            {months.map((m, i) => (
              <g key={m.key}>
                <text x={x(i)} y={H - 4} textAnchor="middle" className={`ax${i === hover ? ' on' : ''}`}>
                  {m.label}
                </text>
                {m.rfqs ? (
                  <circle cx={x(i)} cy={y(m.best)} r={hover === i ? 4 : 2.5} className="dot best" />
                ) : null}
                <rect
                  x={x(i) - step / 2}
                  y={0}
                  width={step}
                  height={H}
                  fill="transparent"
                  onMouseEnter={() => setHover(i)}
                />
              </g>
            ))}
            {h ? <line x1={x(hover!)} x2={x(hover!)} y1={P.t} y2={H - P.b} className="cross" /> : null}
          </svg>
          {h ? (
            <div
              className={`db-tip${hover! > months.length / 2 ? ' left' : ''}`}
              style={{ left: `${(x(hover!) / W) * 100}%` }}
            >
              <b>{h.name}</b>
              {h.rfqs ? (
                <>
                  <span>
                    <i className="ref" /> {t('Referință')} <em>{money(h.reference)}</em>
                  </span>
                  <span>
                    <i className="best" /> {t('Cel mai bun')} <em>{money(h.best)}</em>
                  </span>
                  <span className="g">
                    {rich('Economie posibilă <em>{0}</em>', money(h.reference - h.best))}
                  </span>
                </>
              ) : (
                <small>{t('nicio cerere comparată')}</small>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** Capătul axei, rotunjit frumos: 180.619 → 200.000. */
function niceMax(v: number): number {
  const p = 10 ** Math.floor(Math.log10(v));
  const n = v / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * p;
}
